import { useState, useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/Table';
import { ConfirmModal } from '../ui/Modal';
import { AddOverrideModal } from './AddOverrideModal';
import { deleteTenantOverride, type SignalOverride } from '../../services/tuning';

interface SignalOverridesPanelProps {
  tenantId: string;
  overrides: SignalOverride[];
  onRefresh: () => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const ACTION_VARIANTS: Record<string, 'red' | 'green' | 'yellow'> = {
  remove: 'red',
  add: 'green',
  reweight: 'yellow',
};

export function SignalOverridesPanel({ tenantId, overrides, onRefresh }: SignalOverridesPanelProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SignalOverride | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    await deleteTenantOverride(tenantId, deleteTarget.overrideId);
    setDeleting(false);
    setDeleteTarget(null);
    onRefresh();
  }, [tenantId, deleteTarget, onRefresh]);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Signal Overrides ({overrides.length})</CardTitle>
            <Button variant="secondary" size="sm" onClick={() => setShowAddModal(true)}>
              <Plus className="w-3.5 h-3.5" />
              Add Override
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {overrides.length === 0 ? (
            <p className="text-sm text-text-secondary py-6 text-center">
              No signal overrides configured. Add one to customize how signals are processed for this tenant.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Signal</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Weight</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead><span className="sr-only">Delete</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {overrides.map((o) => (
                  <TableRow key={o.overrideId}>
                    <TableCell>
                      <div className="text-sm">{o.signalType}</div>
                      {o.playbookId !== '*' && (
                        <div className="text-xs text-text-secondary font-mono">{o.playbookId}</div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <span className="text-sm truncate block">{o.signalValue}</span>
                    </TableCell>
                    <TableCell>
                      <Badge variant={ACTION_VARIANTS[o.action] ?? 'gray'}>{o.action}</Badge>
                    </TableCell>
                    <TableCell mono>{o.weightOverride ?? '---'}</TableCell>
                    <TableCell className="max-w-[150px]">
                      <span className="text-xs text-text-secondary truncate block">
                        {o.reason ?? '---'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-text-secondary">{o.createdBy}</TableCell>
                    <TableCell className="text-xs text-text-secondary">{formatDate(o.createdAt)}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => setDeleteTarget(o)}
                        className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-text-secondary hover:text-red-600 transition-colors"
                        title="Delete override"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AddOverrideModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        tenantId={tenantId}
        onAdded={onRefresh}
      />

      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Signal Override"
        message={`Remove the ${deleteTarget?.action} override for "${deleteTarget?.signalValue}"? This takes effect immediately.`}
        confirmLabel="Delete"
        danger
        loading={deleting}
      />
    </>
  );
}
